/**
 * The CC0 packs that ship with the world client (not listed by the approved-asset API). Each
 * group of files has its own LICENSES.md under `public/world/`. CC0 asks for no attribution; the
 * authors are credited anyway.
 */
export interface BuiltinCredit {
  id: string;
  name: string;
  author: string;
  license: "CC0-1.0" | "MIT" | "CC-BY-4.0";
  source: string;
  /** What it is used for. */
  use: string;
  /** Required attribution line (CC BY). */
  attribution?: string;
  /** Real-brand model: hidden when `world.assets.allowBranded` is off. */
  branded?: boolean;
}

export const INTERIOR_CREDITS: readonly BuiltinCredit[] = [
  {
    id: "kenney-furniture",
    name: "Furniture Kit",
    author: "Kenney",
    license: "CC0-1.0",
    source: "https://opengameart.org/content/furniture-kit",
    use: "Office chairs, monitors, keyboards, coffee tables, fridge (interiors)",
  },
  {
    id: "polyhaven-furniture",
    name: "Poly Haven furniture and plants",
    author:
      "Vibrant Nordic, Ulan Cabanilla, Arrangemonk, Benny Weimer, Rico Cilliers, Kirill Sannikov, James Ray Cock, ParzivalCG, Joe Seabuhr",
    license: "CC0-1.0",
    source: "https://polyhaven.com/models",
    use: "Desks, armchairs, sofa, shelves, plants, meeting table and chairs, lamps (interiors)",
  },
  {
    id: "ambientcg-interior",
    name: "Office surfaces: carpet, wood floor, plaster, concrete, acoustic ceiling",
    author: "ambientCG",
    license: "CC0-1.0",
    source: "https://ambientcg.com",
    use: "Floors, walls and ceilings (interiors)",
  },
];

/** The Poly Haven pack of the themed Uzmoon floors: models, PBR surface sets and the city HDRI (CC0). */
export const PLAZA_INTERIOR_CREDITS: readonly BuiltinCredit[] = [
  {
    id: "polyhaven-plaza-models",
    name: "Poly Haven furniture, art and plants (29 models)",
    author:
      "Dairon Sanchez, John Malcolm, Rico Cilliers, Tina, Rob Tuytel, James Ray Cock, Ulan Cabanilla, Yann Kervran, Kuutti Siitonen, Amin, Patrik Pangerl, Caspian Fortune, PeterM, Fran Calvente, Jurita Burger, Serhii Khromov, Jenelle van Heerden",
    license: "CC0-1.0",
    source: "https://polyhaven.com/models",
    use: "Sofas, lounge chairs, cabinets, shelves, art, plants and decor on the themed floors",
  },
  {
    id: "polyhaven-plaza-surfaces",
    name: "Poly Haven surfaces: granite, marble, terrazzo, parquet, oak, walnut, velvet, concrete, plaster",
    author:
      "Charlotte Baglioni, Rob Tuytel, Jenelle van Heerden, Sergej Majboroda, Amal Kumar, Dimitrios Savva, colormass, Rico Cilliers",
    license: "CC0-1.0",
    source: "https://polyhaven.com/textures",
    use: "Floors, walls and feature walls of the themed floors (15 sets)",
  },
  {
    id: "polyhaven-neuer-zollhof",
    name: "Neuer Zollhof (HDRI)",
    author: "Greg Zaal",
    license: "CC0-1.0",
    source: "https://polyhaven.com/a/neuer_zollhof",
    use: "Indoor reflections (city image at low intensity)",
  },
];

/** The exterior and the living city: trees, vehicles, street furniture and pedestrians (Quaternius). */
const quaternius = (id: string, name: string, slug: string, use: string): BuiltinCredit => ({
  id: `quaternius-${id}`,
  name,
  author: "Quaternius",
  license: "CC0-1.0",
  source: `https://quaternius.com/packs/${slug}.html`,
  use,
});

export const CITY_CREDITS: readonly BuiltinCredit[] = [
  quaternius("nature", "Ultimate Nature Pack", "ultimatenature", "Trees and bushes (exterior)"),
  quaternius("cars", "Cars", "cars", "Cars driving on the streets"),
  quaternius(
    "transport",
    "Public Transport",
    "publictransport",
    "Buses, bicycles and traffic cones",
  ),
  quaternius(
    "streets",
    "Modular Streets",
    "modularstreets",
    "Street lamps, traffic lights and road signs",
  ),
  quaternius(
    "props",
    "Ultimate Home Interior",
    "ultimatehomeinterior",
    "Trash cans on the sidewalks",
  ),
  quaternius(
    "characters",
    "Ultimate Modular Characters",
    "ultimatemodularcharacters",
    "Animated pedestrians (Walk and Idle clips)",
  ),
];

/** The realistic human characters: the user's avatar, the assistant, the agents in the floors. */
export const CHARACTER_CREDITS: readonly BuiltinCredit[] = [
  quaternius(
    "universal-base-characters",
    "Universal Base Characters",
    "universalbasecharacters",
    "Classic human bodies, hairstyles and eyes (the classic character, the assistant's look, agents)",
  ),
  quaternius(
    "universal-animation-library",
    "Universal Animation Library (1 and 2)",
    "universalanimationlibrary",
    "Idle, walk, jog, sprint, jump and sitting animations; the backward, sideways and turning steps are made from them, also for the office people",
  ),
];

/**
 * The office workers on the floors: Microsoft Rocketbox avatars and motion-capture clips (MIT,
 * "Copyright (c) 2020 Microsoft", pinned commit 0943055d). The license text ships in
 * `public/world/models/rocketbox/LICENSE` and `public/world/anim/rocketbox/LICENSE`.
 */
export const ROCKETBOX_CREDITS: readonly BuiltinCredit[] = [
  {
    id: "microsoft-rocketbox-avatars",
    name: "Rocketbox avatars (18 office looks)",
    author: "Microsoft",
    license: "MIT",
    source: "https://github.com/microsoft/Microsoft-Rocketbox",
    use: "Your character by default, and the agents seated at their desks on the floors",
  },
  {
    id: "microsoft-rocketbox-animations",
    name: "Rocketbox motion-capture clips (seated)",
    author: "Microsoft",
    license: "MIT",
    source: "https://github.com/microsoft/Microsoft-Rocketbox",
    use: "Sitting, breathing, thinking, looking around and stretching clips of the agents",
  },
];

/**
 * The MakeHuman character kit (`public/world/models/mh/`, its LICENSES.md lists every item with
 * pack, author and licence). CC0 1.0: the base mesh, targets, rig and the community packs.
 */
const mhPack = (id: string, name: string, author: string, use: string): BuiltinCredit => ({
  id: `makehuman-${id}`,
  name,
  author,
  license: "CC0-1.0",
  source: `https://static.makehumancommunity.org/assets/assetpacks/${id}.html`,
  use,
});
export const MAKEHUMAN_CREDITS: readonly BuiltinCredit[] = [
  {
    id: "makehuman-mpfb",
    name: "MakeHuman base mesh, morph targets and game_engine rig (MPFB 2)",
    author: "MakeHuman team (Data Collection AB, Joel Palmius, Jonas Hauquier)",
    license: "CC0-1.0",
    source: "https://github.com/makehumancommunity/mpfb2",
    use: "Your character's body, its 45 body and face sliders and its skeleton",
  },
  mhPack(
    "makehuman_system_assets",
    "MakeHuman system assets",
    "MakeHuman team",
    "Skins, eyes, eyebrows, eyelashes, hair, suits, jeans, shirts and shoes",
  ),
  mhPack("skins01", "Skins 01 (natural female skins)", "Margaret Toigo, cutoff3d", "Skin tones"),
  mhPack("skins02", "Skins 02 (natural male skins)", "Mindfront, Margaret Toigo", "Skin tones"),
  mhPack(
    "suits01",
    "Suits 01 (formal suits)",
    "Margaret Toigo",
    "Business jackets, trousers and skirts",
  ),
  mhPack(
    "shirts01",
    "Shirts 01",
    "Margaret Toigo, Namuhekam, Joel Palmius",
    "T-shirts, polo, sweater, turtleneck",
  ),
  mhPack("pants01", "Pants 01", "Margaret Toigo, Cortu", "Chinos, cargo trousers, shorts"),
  mhPack("shoes01", "Shoes 01", "Margaret Toigo", "Boots, flats, booties"),
  mhPack("glasses01", "Glasses 01", "spamrakuen", "Glasses"),
  mhPack("hats01", "Hats 01", "jujube", "Newsboy cap"),
  mhPack(
    "bodyparts05",
    "Bodyparts 05 (beards)",
    "WDG, Rehman Polanski, grinsegold, culturalibre",
    "Beards and moustache",
  ),
];

/**
 * CC BY 4.0 vehicles (`public/world/models/vehicles-ccby/`, see its LICENSES.md and catalog.json).
 * The attribution lines are required by the license; changes: converted and optimised for web.
 */
export const VEHICLE_CREDITS: readonly BuiltinCredit[] = [
  {
    id: "vehicle-bicycle-classic",
    name: "Classic Bicycle | LowPoly | Game ready",
    author: "by__Rx",
    license: "CC-BY-4.0",
    source:
      "https://sketchfab.com/3d-models/classic-bicycle-lowpoly-game-ready-da6507928c0c4a5c8776008cdb9f747e",
    use: "Drivable vehicle: Classic Bicycle",
    attribution:
      '"Classic Bicycle | LowPoly | Game ready" by by__Rx (https://sketchfab.com/3d-models/classic-bicycle-lowpoly-game-ready-da6507928c0c4a5c8776008cdb9f747e) is licensed under CC BY 4.0. Changes: converted/optimised for web.',
  },
  {
    id: "vehicle-bus-town",
    name: "Generic Town Bus",
    author: "own.guest",
    license: "CC-BY-4.0",
    source: "https://sketchfab.com/3d-models/generic-town-bus-14fe03d792914d51b6c6250b393c44fd",
    use: "Drivable vehicle: Town Bus",
    attribution:
      '"Generic Town Bus" by own.guest (https://sketchfab.com/3d-models/generic-town-bus-14fe03d792914d51b6c6250b393c44fd) is licensed under CC BY 4.0. Changes: converted/optimised for web.',
  },
  {
    id: "vehicle-generic-passenger-car-pack",
    name: "Generic passenger car pack (Generic Compact, Generic Coupe, Generic Sedan, Generic SUV, Generic Minivan)",
    author: "Comrade1280",
    license: "CC-BY-4.0",
    source:
      "https://sketchfab.com/3d-models/generic-passenger-car-pack-20f9af9b8a404d5cb022ac6fe87f21f5",
    use: "Drivable vehicle: Generic Compact, Generic Coupe, Generic Sedan, Generic SUV, Generic Minivan",
    attribution:
      '"Generic passenger car pack" by Comrade1280 (https://sketchfab.com/3d-models/generic-passenger-car-pack-20f9af9b8a404d5cb022ac6fe87f21f5) is licensed under CC BY 4.0. Changes: converted/optimised for web.',
  },
  {
    id: "vehicle-classic-e30",
    name: "Low Poly Car - BMW E30 1985 White",
    author: "ROH3D",
    license: "CC-BY-4.0",
    source:
      "https://sketchfab.com/3d-models/low-poly-car-bmw-e30-1985-white-9dea494b447e442fafbddfc7eccbf158",
    use: "Drivable vehicle: Classic E30 (BMW)",
    attribution:
      '"Low Poly Car - BMW E30 1985 White" by ROH3D (https://sketchfab.com/3d-models/low-poly-car-bmw-e30-1985-white-9dea494b447e442fafbddfc7eccbf158) is licensed under CC BY 4.0. Changes: converted/optimised for web.',
    branded: true,
  },
  {
    id: "vehicle-cop-fairheaven80",
    name: "Fairheaven LT '80 Cop Cruiser - Low poly model",
    author: "Daniel Zhabotinsky",
    license: "CC-BY-4.0",
    source:
      "https://sketchfab.com/3d-models/fairheaven-lt-80-cop-cruiser-low-poly-model-61766c1e6e1e4b2b803c174f33433a74",
    use: "Drivable vehicle: Fairheaven Cop Cruiser",
    attribution:
      '"Fairheaven LT \'80 Cop Cruiser - Low poly model" by Daniel Zhabotinsky (https://sketchfab.com/3d-models/fairheaven-lt-80-cop-cruiser-low-poly-model-61766c1e6e1e4b2b803c174f33433a74) is licensed under CC BY 4.0. Changes: converted/optimised for web.',
  },
  {
    id: "vehicle-scooter-electric",
    name: "Electric Scooter (Low-poly, game-ready)",
    author: "Delandi",
    license: "CC-BY-4.0",
    source:
      "https://sketchfab.com/3d-models/electric-scooter-low-poly-game-ready-e22bffc784b04a93acc83599fa7a81c0",
    use: "Drivable vehicle: Electric Scooter",
    attribution:
      '"Electric Scooter (Low-poly, game-ready)" by Delandi (https://sketchfab.com/3d-models/electric-scooter-low-poly-game-ready-e22bffc784b04a93acc83599fa7a81c0) is licensed under CC BY 4.0. Changes: converted/optimised for web.',
  },
  {
    id: "vehicle-sport-concept",
    name: "Car Concept",
    author: "Eric Chadwick (model); (c) 2024 Darmstadt Graphics Group GmbH",
    license: "CC-BY-4.0",
    source: "https://github.com/KhronosGroup/glTF-Sample-Assets/tree/main/Models/CarConcept",
    use: "Drivable vehicle: Car Concept",
    attribution:
      "Car Concept by Eric Chadwick, (c) 2024 Darmstadt Graphics Group GmbH, CC BY 4.0. Remove Khronos / 3D Commerce logo meshes before shipping (trademark notice).",
  },
  {
    id: "vehicle-sport-phoenix455",
    name: "Phoenix 455 '71 - Low poly model",
    author: "Daniel Zhabotinsky",
    license: "CC-BY-4.0",
    source:
      "https://sketchfab.com/3d-models/phoenix-455-71-low-poly-model-955edc733c6d44fabc0ad7c246a15896",
    use: "Drivable vehicle: Phoenix 455",
    attribution:
      '"Phoenix 455 \'71 - Low poly model" by Daniel Zhabotinsky (https://sketchfab.com/3d-models/phoenix-455-71-low-poly-model-955edc733c6d44fabc0ad7c246a15896) is licensed under CC BY 4.0. Changes: converted/optimised for web.',
  },
  {
    id: "vehicle-sport-saba95",
    name: "Saba V12 '95 - Low poly model",
    author: "Daniel Zhabotinsky",
    license: "CC-BY-4.0",
    source:
      "https://sketchfab.com/3d-models/saba-v12-95-low-poly-model-3ab977050c334c13b6abb5de078b4957",
    use: "Drivable vehicle: Saba V12",
    attribution:
      '"Saba V12 \'95 - Low poly model" by Daniel Zhabotinsky (https://sketchfab.com/3d-models/saba-v12-95-low-poly-model-3ab977050c334c13b6abb5de078b4957) is licensed under CC BY 4.0. Changes: converted/optimised for web.',
  },
  {
    id: "vehicle-suv-lightbody85",
    name: "Lightbody Lifted '85 - Low poly model",
    author: "Daniel Zhabotinsky",
    license: "CC-BY-4.0",
    source:
      "https://sketchfab.com/3d-models/lightbody-lifted-85-low-poly-model-67beae18c3d24be68f9c8f0ec382d8e3",
    use: "Drivable vehicle: Lightbody Lifted",
    attribution:
      '"Lightbody Lifted \'85 - Low poly model" by Daniel Zhabotinsky (https://sketchfab.com/3d-models/lightbody-lifted-85-low-poly-model-67beae18c3d24be68f9c8f0ec382d8e3) is licensed under CC BY 4.0. Changes: converted/optimised for web.',
  },
  {
    id: "vehicle-taxi-canyon75",
    name: "Canyon '75 Taxi - Low poly model",
    author: "Daniel Zhabotinsky",
    license: "CC-BY-4.0",
    source:
      "https://sketchfab.com/3d-models/canyon-75-taxi-low-poly-model-9e8f92a215784f3d8aaaaeab1bef54c4",
    use: "Drivable vehicle: Canyon 75 Taxi",
    attribution:
      '"Canyon \'75 Taxi - Low poly model" by Daniel Zhabotinsky (https://sketchfab.com/3d-models/canyon-75-taxi-low-poly-model-9e8f92a215784f3d8aaaaeab1bef54c4) is licensed under CC BY 4.0. Changes: converted/optimised for web.',
  },
];

/** Poly Haven street furniture and vegetation, ambientCG pavement and leaf atlases (all CC0). */
export const STREET_NATURE_CREDITS: readonly BuiltinCredit[] = [
  {
    id: "polyhaven-street-props",
    name: "Poly Haven street props: street lamps, fire hydrant, trash can, road barrier, chain-link fence, benches, utility boxes",
    author: "Josh Dean, Amal Kumar, Stuart Attenborrow, Kirill Sannikov, Rob Tuytel and others",
    license: "CC0-1.0",
    source: "https://polyhaven.com/models",
    use: "Streets, plazas and parks of the world (models/ph-street)",
  },
  {
    id: "polyhaven-nature",
    name: "Poly Haven vegetation: quiver tree, shrubs, potted plant",
    author: "Rico Cilliers, Dario Barresi, James Ray Cock and others",
    license: "CC0-1.0",
    source: "https://polyhaven.com/models",
    use: "Trees and plants with three levels of detail (models/ph-nature)",
  },
  {
    id: "ambientcg-street",
    name: "ambientCG surfaces: Asphalt031, PavingStones070, Concrete049, LeafSet030",
    author: "ambientCG (Lennart Demes)",
    license: "CC0-1.0",
    source: "https://ambientcg.com",
    use: "Roads, pavement and leaf atlases (textures/ambientcg)",
  },
];

/** Every built-in pack of the world, for the credits page. */
export const BUILTIN_CREDITS: readonly BuiltinCredit[] = [
  ...INTERIOR_CREDITS,
  ...PLAZA_INTERIOR_CREDITS,
  ...CITY_CREDITS,
  ...MAKEHUMAN_CREDITS,
  ...CHARACTER_CREDITS,
  ...ROCKETBOX_CREDITS,
  ...VEHICLE_CREDITS,
  ...STREET_NATURE_CREDITS,
];
